import clsx from 'clsx';
import {
    memo,
    MouseEventHandler,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { createPortal } from 'react-dom';

type Props = PropsWithChildren<{
    className?: string;
    opened: boolean;
    size?: 'sm' | 'md' | 'lg';
    danger?: boolean;
    fullScreen?: boolean;
    fullHeight?: boolean;
    shaded?: boolean;
    handleClose: () => void;
    root: Element;
}>

export const Modal = memo((
    {
        opened,
        danger = false,
        fullScreen = false,
        fullHeight = false,
        shaded = false,
        size = 'md',
        handleClose,
        children,
        root,
        className,
    }: Props,
) => {
    const modalRef = useRef<HTMLDivElement | null>(null);

    const handleOverlayClick: MouseEventHandler<HTMLDivElement> = (event) => {
        const target = event.target as Node;
        event.stopPropagation();

        if (!modalRef.current?.contains(target)) {
            handleClose();
        }
    };

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape' && opened) {
            handleClose();
        }
    }, [handleClose, opened]);

    useEffect(() => {
        document.body.classList.toggle('vss-overflow-hidden', opened);

        return () => {
            document.body.classList.remove('vss-overflow-hidden');
        };
    }, [opened]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    return root ? createPortal(
        <div
            className={clsx(
                {
                    'vss-modal-overlay': true,
                    'vss-modal-overlay--visible': opened,
                },
                className,
            )}
            onMouseDown={handleOverlayClick}
            role="presentation"
            aria-hidden={!opened}
        >
            <div
                ref={modalRef}
                className={clsx({
                    'vss-modal': true,
                    [`vss-modal--${size}`]: true,
                    'vss-modal--visible': opened,
                    'vss-modal--danger': danger,
                    'vss-modal--fullScreen': fullScreen,
                    'vss-modal--shaded': shaded,
                    'vss-modal--full-height': fullHeight,
                })}
            >{children}
            </div>
        </div>,
        root,
    ) : null;
});
